import { useEffect, useRef, useState } from "react";
import {
  AlertCircle,
  ArrowLeft,
  FileText,
  Loader2,
  LockKeyhole,
  Monitor,
  Paperclip,
  RotateCcw,
} from "lucide-react";
import {
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  Select,
} from "../../design-system";
import { AiModelSelect } from "./llm-model-select";
import type {
  RetryRunPatch,
  RetryRunResult,
  RetryRunView,
} from "./retry-run-data";
import "./retry-run.css";

export type RetryRunDialogProps = {
  open: boolean;
  view: RetryRunView;
  onClose: () => void;
  /** Reject with the server's Error; the draft remains intact on failure. */
  onRetry: (patch: RetryRunPatch) => Promise<RetryRunResult | void>;
  onDone?: (result: RetryRunResult | void) => void;
  onReload?: () => void;
};

/** One replacement surface for RunDetail. API, role checks and option ownership stay outside. */
export function RetryRunDialog(props: RetryRunDialogProps) {
  if (!props.open) return null;
  // A new run gets a fresh draft; same-run revalidation must not erase an edited draft.
  return <RetryRunForm key={props.view.context.runId} {...props} />;
}

function RetryRunForm({
  open,
  view,
  onClose,
  onRetry,
  onDone,
  onReload,
}: RetryRunDialogProps) {
  const ready = view.status === "ready" ? view : null;
  const [draft, setDraft] = useState<{
    model: string;
    machine: string;
    device: string;
  } | null>(null);
  const values = draft ?? {
    model: ready?.settings.llmModel ?? "",
    machine: ready?.settings.machineId ?? "",
    device: ready?.settings.deviceSerial ?? "",
  };
  const { model, machine, device } = values;
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [deviceReset, setDeviceReset] = useState(false);
  const [cleanupDrafts, setCleanupDrafts] = useState(true);
  const mounted = useRef(true);
  const pending = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const locked = !ready?.settings.editable || !view.canRetry || busy;
  const options =
    ready?.devices.filter((item) => !machine || item.machineId === machine) ??
    [];
  const missingMachine =
    !!machine && !ready?.machines.some((item) => item.id === machine);
  const missingDevice =
    !!device && !options.some((item) => item.serial === device);
  async function submit() {
    if (!ready || !view.canRetry || pending.current) return;
    const patch: RetryRunPatch = {};
    if (ready.settings.editable) {
      if (model !== (ready.settings.llmModel ?? ""))
        patch.llmModel = model || null;
      if (machine !== (ready.settings.machineId ?? ""))
        patch.machineId = machine || null;
      if (device !== (ready.settings.deviceSerial ?? ""))
        patch.deviceSerial = device || null;
    }
    // This per-attempt option remains editable even when model/assignment settings are locked.
    if (ready.settings.draftCleanup && !cleanupDrafts)
      patch.cleanupDrafts = false;
    pending.current = true;
    setBusy(true);
    setError("");
    try {
      const result = await onRetry(patch);
      if (mounted.current) onDone?.(result);
    } catch (caught) {
      if (mounted.current)
        setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      pending.current = false;
      if (mounted.current) setBusy(false);
    }
  }
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !busy) onClose();
      }}
      title="설정 바꿔 재시도"
      description="같은 작업을 새 시도로 실행합니다. 변경 내용은 새 시도의 실행 기록에 남습니다."
      size="wide"
      contentPadding="none"
      footer={
        <DialogActions
          secondary={{
            label: (
              <>
                <ArrowLeft size={16} aria-hidden="true" /> 실행 상세로
              </>
            ),
            onClick: onClose,
            disabled: busy,
          }}
          primary={{
            label: busy ? (
              <>
                <Loader2 size={16} className="spin" aria-hidden="true" /> 보내는
                중
              </>
            ) : (
              <>
                <RotateCcw size={16} aria-hidden="true" /> 재시도
              </>
            ),
            onClick: submit,
            disabled: !ready || !view.canRetry || busy,
            "aria-busy": busy,
          }}
        />
      }
    >
      <div className="mcr-retry" aria-busy={busy || view.status === "loading"}>
        <div className="mcr-retry-run">
          <span>{view.context.runId}</span>
          <strong>{view.context.title}</strong>
          <Badge tone="blue">새 시도</Badge>
        </div>
        {!view.canRetry && (
          <div className="mcr-retry-notice is-error" role="alert">
            <LockKeyhole size={18} aria-hidden="true" />
            <p>재시도 권한이 없습니다. 관리자에게 권한을 요청하세요.</p>
          </div>
        )}
        {view.status === "loading" ? (
          <div className="mcr-retry-state" role="status">
            <Loader2 size={22} className="spin" aria-hidden="true" />
            <strong>재시도 설정을 불러오는 중</strong>
            <span>현재 모델과 실행 배정을 확인합니다.</span>
          </div>
        ) : view.status === "error" ? (
          <div className="mcr-retry-state is-error" role="alert">
            <AlertCircle size={24} aria-hidden="true" />
            <p>{view.error}</p>
            {onReload && (
              <Button variant="secondary" onClick={onReload}>
                다시 불러오기
              </Button>
            )}
          </div>
        ) : (
          <>
            {!view.settings.editable && (
              <div className="mcr-retry-notice" role="status">
                <LockKeyhole size={18} aria-hidden="true" />
                <p>
                  {view.settings.reason ||
                    "이 실행의 설정은 변경할 수 없습니다."}
                  <br />
                  <strong>같은 설정으로 재시도만 할 수 있습니다.</strong>
                </p>
              </div>
            )}
            {view.settings.editable && view.settings.reason && (
              <div className="mcr-retry-notice" role="status">
                <AlertCircle size={18} aria-hidden="true" />
                <p>{view.settings.reason}</p>
              </div>
            )}
            <section
              className="mcr-retry-section"
              aria-label="재시도 실행 설정"
            >
              <div className="mcr-retry-section-title">
                <h3>실행 설정</h3>
                <span>
                  {view.settings.editable
                    ? "이번 재시도에 적용할 설정"
                    : "현재 설정 유지"}
                </span>
              </div>
              <AiModelSelect
                models={view.models}
                value={model}
                onChange={(next) => setDraft({ ...values, model: next })}
                disabled={locked}
                alwaysShow
              />
              <div className="mcr-retry-grid">
                <Select
                  label="머신"
                  value={machine}
                  disabled={locked}
                  onChange={(event) => {
                    const next = event.target.value;
                    const resetDevice = !!device && next !== machine;
                    setDraft({
                      ...values,
                      machine: next,
                      device: resetDevice ? "" : device,
                    });
                    if (resetDevice) setDeviceReset(true);
                  }}
                >
                  <option value="">자동 배정</option>
                  {view.machines.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.label}
                      {item.connected ? " · 연결됨" : " · 연결 끊김"}
                    </option>
                  ))}
                  {missingMachine && (
                    <option value={machine}>
                      현재 설정 · {machine} (목록에 없음)
                    </option>
                  )}
                </Select>
                <Select
                  label="디바이스"
                  value={device}
                  disabled={locked}
                  onChange={(event) => {
                    setDraft({ ...values, device: event.target.value });
                    setDeviceReset(false);
                  }}
                >
                  <option value="">자동 배정</option>
                  {options.map((item) => (
                    <option key={item.serial} value={item.serial}>
                      {item.machineLabel} · {item.label}
                    </option>
                  ))}
                  {missingDevice && (
                    <option value={device}>
                      현재 설정 · {device} (목록에 없음)
                    </option>
                  )}
                </Select>
              </div>
              {deviceReset && (
                <p className="mcr-retry-hint" role="status">
                  머신을 변경하여 디바이스가 자동 배정으로 바뀌었습니다.
                </p>
              )}
              {(missingMachine || missingDevice) && (
                <p className="mcr-retry-hint">
                  현재 배정이 선택 목록에 없어도 값을 유지합니다. 실행 가능
                  여부는 요청 후 확인됩니다.
                </p>
              )}
              {view.settings.draftCleanup && (
                <div className="mcr-retry-cleanup">
                  <Checkbox
                    label="이전 시도의 임시저장 글 지우기 (같은 제목만)"
                    checked={cleanupDrafts}
                    onChange={setCleanupDrafts}
                    disabled={!view.canRetry || busy}
                  />
                  <p className="mcr-retry-hint">
                    이번 시도에만 적용합니다. 글쓰기 전에 같은 제목의 이전 시도
                    임시 글을 정리합니다.
                  </p>
                </div>
              )}
              {view.connectedMachineCount === 0 && (
                <div className="mcr-retry-notice is-warning" role="status">
                  <Monitor size={18} aria-hidden="true" />
                  <p>
                    <strong>
                      {view.context.execution.name}에 연결된 PC가 없습니다.
                    </strong>
                    <br />
                    재시도 요청은 가능하며, PC가 연결될 때까지 배정 대기로
                    남습니다.
                  </p>
                </div>
              )}
            </section>
          </>
        )}
        <section
          className="mcr-retry-section is-readonly"
          aria-label="변경할 수 없는 작업 정보"
        >
          <div className="mcr-retry-section-title">
            <h3>
              <LockKeyhole size={15} aria-hidden="true" /> 작업 정보
            </h3>
            <span>읽기 전용</span>
          </div>
          <dl className="mcr-retry-context">
            <div>
              <dt>실행 고객사</dt>
              <dd>{view.context.execution.name}</dd>
            </div>
            <div>
              <dt>대상 고객사</dt>
              <dd>{view.context.target.name}</dd>
            </div>
            <div>
              <dt>작업 유형</dt>
              <dd>{view.context.typeName}</dd>
            </div>
          </dl>
          <details className="mcr-retry-manuscript">
            <summary>
              <FileText size={16} aria-hidden="true" />
              <span>{view.context.manuscript?.title ?? "원고 없음"}</span>
              <small>원고·첨부 확인</small>
            </summary>
            <div>
              <pre>
                {view.context.manuscript?.body ?? "등록된 원고가 없습니다."}
              </pre>
              <ul aria-label="첨부 파일">
                {view.context.attachments.length ? (
                  view.context.attachments.map((file) => (
                    <li key={file.fileId}>
                      <Paperclip size={14} aria-hidden="true" />
                      {file.name}
                    </li>
                  ))
                ) : (
                  <li>첨부 없음</li>
                )}
              </ul>
            </div>
          </details>
          <p className="mcr-retry-hint">
            실행 기록·사용량·SEO 기록이 이 작업에 묶여 있어 바꿀 수 없습니다. 새
            작업으로 등록하세요.
          </p>
        </section>
        {error && (
          <div className="mcr-retry-notice is-error" role="alert">
            <AlertCircle size={18} aria-hidden="true" />
            <p>{error}</p>
          </div>
        )}
      </div>
    </Dialog>
  );
}
